<template>
  <div class="press-category-wrapper">
    <Head>
      <Title>{{ data.tag.name }}</Title>
      <Link
        v-for="(link, index) in hrefLang()"
        :key="index"
        :rel="link.rel"
        :href="link.href"
        :hid="link.hid"
        :hreflang="link.hreflang"
      />
    </Head>

    <h1 class="h4 text-center opacity-muted mb-semi">
      #{{ data.name }}
    </h1>
    <PressArticleList :items="data.articles" />
    <template v-if="data.hasMore">
      <hr class="mt-semi border-top">
      <button
        :disabled="data.loading"
        class="btn relative m-auto block xl:px hover:bg-dusty-gray"
        @click="loadMore"
      >
        <div v-if="data.loading" class="spinner mr-micro" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        {{ $messages('press.load_more_articles') }}
      </button>
    </template>
  </div>
</template>
<script setup>
import { pressStore } from '~/store/press'
import { isEmpty } from '@/utils/comparators'
import { ARTICLE_LOAD_NUMBER } from '@/utils/press-options'

definePageMeta({
  layout: 'press',
  stopDefaultSwitch: true
})

const locale = useLocale()
const route = useRoute()
const pStore = pressStore()
const { $env, $bus, $mRouter, $messages, $track } = useNuxtApp()

const data = reactive({
  takeStep: ARTICLE_LOAD_NUMBER,
  take: ARTICLE_LOAD_NUMBER,
  loading: false,
  articles: [],
  hasMore: false,
  name: '',
  tagSlug: '',
  tagId: null,
  page: 1,
  tag: null
})

const setupData = async () => {
  await pStore.loadTagPage({ take: ARTICLE_LOAD_NUMBER, locale, slug: route.params.slug })
  data.tag = pStore.getTagBySlug(route.params.slug)
  data.name = data.tag.name
  data.tagSlug = data.tag.slug
  data.tagId = data.tag.id
  data.articles = pStore.getArticlesByTag(data.take, data.tagSlug)
  data.hasMore = pStore.hasMoreArticlesInTag(data.articles.length, data.tagSlug)
}

await setupData()

watch(route, async () => {
  await setupData()
})

const getAlternativeSlug = (locale) => {
  const filtered = data.tag.alternatives.filter(a => a.locale === locale)
  return filtered.length > 0 ? filtered[0].value : ''
}

$bus.onDynamicRouteChangeLanguage(route.meta.key, (val) => {
  const slug = getAlternativeSlug(val.destinationLocale)
  !isEmpty(slug)
    ? window.location.href = $mRouter.resolveLocalRoute({ name: 'press-tag-slug', params: { slug } }).href
    : useRouter().push($mRouter.getLocalRoute({ name: 'press' }))
})

const hrefLang = () => {
  const alternatives = []
  if (data.tag.alternatives) {
    data.tag.alternatives.filter(c => !isNullOrUndefined(c.value)).forEach((alternative) => {
      if (alternative.locale === 'en') {
        alternatives.push({
          hid: 'default-alternate',
          rel: 'alternate',
          hreflang: 'x-default',
          href: $env.websiteUrl + $mRouter.getLocalRoute({ name: 'press-tag-slug', params: { slug: alternative.value } }).href
        })
        alternatives.push({
          hid: 'en-alternate',
          rel: 'alternate',
          hreflang: alternative.locale,
          href: $env.websiteUrl + $mRouter.getLocalRoute({ name: 'press-tag-slug', params: { slug: alternative.value } }).href
        })
      } else {
        alternatives.push({
          hid: `${alternative.locale}-alternate`,
          rel: 'alternate',
          hreflang: alternative.locale,
          href: $env.websiteUrl + $mRouter.getLocalRoute({ name: 'press-tag-slug', params: { slug: alternative.value } }).href
        })
      }
    })
  }
  return alternatives
}

const loadMore = async () => {
  data.loading = true
  const _take = data.take + data.takeStep
  await pStore.loadArticlesByTag({ take: _take, locale, tagId: data.tagId, slug: data.tagSlug })
  data.take = _take
  data.loading = false
}

onMounted(() => {
  $track.pageView(route, {
    name: `Press Tag - ${data.tag.name}`,
    component: `/press/tag/${route.params.slug}/`
  })
})

</script>
